<template>
  <section class="resource-page">
    <header>
      <p class="resource-title is-size-6 has-text-weight-bold has-text-primary is-uppercase">{{ $t('resource.explore.title') }}</p>
      <h2 class="is-size-3 has-text-weight-semibold has-text-black gate-way-title">{{ $t('resource.explore.subTitle') }}</h2>
      <ul class="category-row is-flex">
        <li v-for="category in resourceCategories" :key="category.slug"
             class="cursor-pointer category-container" :class="{'is-inactive': hasSelectedCategory && selectedCategory.slug !== category.slug}"
             @click.prevent="toggleSelectedCategory(category)">
          <div class="category-image-shadow">
            <div class="category-image-circle">
              <img :src="proxyImage(category.image)" :alt="category.slug">
            </div>
          </div>
          <p class="category-title has-text-weight-semibold">{{ $t(category.title) }}</p>
        </li>
      </ul>
    </header>
    <section class="content-area">
      <ul v-if="hasSelectedCategory" class="sort-options is-flex">
        <li v-for="(sortOption, index) in selectedCategory.sortOptions" :key="index"
             class="sort-option has-text-weight-semibold cursor-pointer is-uppercase"
             :class="{'selected': selectedSort === sortOption}"
             @click="selectSortOption(sortOption)">
          {{ $t(sortOption) }}
        </li>
      </ul>
      <h2 class="coming-soon" v-if="selectedCategory.slug === 'toolsAndSoftware' && selectedSort === 'Software'">
        COMING SOON
      </h2>
      <infinite-scroll :callback="loadMore" class="proj-cards-container">
        <transition-group name="projects" tag="div" class="proj-cards-group">
          <resource-card v-for="project in projects" :key="project.id" class="proj-card"
                v-on:click.native="openProject(project)" :image="project.image" :url="project.url"
               :title="getCardTitle(project)" :subtitle="getCardSubTitle(project)" :isResources="true" :isYscWebinar="false">
          </resource-card>
        </transition-group>
      </infinite-scroll>
      <div v-if="loading"><span class="loader"></span></div>
    </section>
    <section class="feedback-messages" :class="{'is-mobile-browser': isMobile}">
      <p v-if="error !== ''" class="has-text-danger is-size-5">{{ error }}</p>
      <p v-if="message !== ''" class="has-text-primary is-size-5">{{ message }}</p>
    </section>
  </section>
</template>

<script>

import ResourceCard from './ResourceCard'
import InfiniteScroll from '@/components/Controls/InfiniteScroll'

import imageProxy from '@/libs/image-proxy'
import userAgent from '@/libs/user-agent'

import moment from 'moment-timezone'
import utmTracking from '@/mixins/utm-tracking'

const WEBINAR_TITLE = 'resource.explore.resourceCategories.webinar.title'
const UPCOMING = 'resource.explore.resourceCategories.webinar.sortOptions.upcoming'
const RECORDED = 'resource.explore.resourceCategories.webinar.sortOptions.recorded'

const EBOOK_TITLE = 'resource.explore.resourceCategories.ebooksAndPapers.title'
const EBOOK_ALL = 'resource.explore.resourceCategories.ebooksAndPapers.sortOptions.all'

const PROJECTS_TITLE = 'resource.explore.resourceCategories.projects.title'
const PROJECTS_ALL = 'resource.explore.resourceCategories.projects.sortOptions.all'

const GAMES_TITLE = 'resource.explore.resourceCategories.games.title'
const POPULAR = 'resource.explore.resourceCategories.games.sortOptions.popular'
const GAMES_ALL = 'resource.explore.resourceCategories.games.sortOptions.all'

const TOOLS_AND_SOFTWARE_TITLE = 'resource.explore.resourceCategories.toolsAndSoftware.title'
const TOOLS_ALL = 'resource.explore.resourceCategories.toolsAndSoftware.sortOptions.all'
const SOFTWARE = 'resource.explore.resourceCategories.toolsAndSoftware.sortOptions.software'
const CALCULATORS = 'resource.explore.resourceCategories.toolsAndSoftware.sortOptions.calculators'

const resourceCategories = [
  {
    title: WEBINAR_TITLE,
    slug: 'webinars',
    image: '/static/categories/stories/resources/webinars.jpg',
    dispatchMethod: 'getResourceByType',
    sortOptions: [UPCOMING, RECORDED]
  },
  {
    title: EBOOK_TITLE,
    slug: 'ebooksAndPapers',
    image: '/static/categories/stories/resources/ebooksAndPapers.jpg',
    dispatchMethod: 'getResourceByType',
    sortOptions: [EBOOK_ALL]
  },
  {
    title: PROJECTS_TITLE,
    slug: 'projects',
    image: '/static/categories/stories/resources/projects.png',
    dispatchMethod: 'getResourceByType',
    sortOptions: [PROJECTS_ALL]
  },
  {
    title: TOOLS_AND_SOFTWARE_TITLE,
    slug: 'toolsAndSoftware',
    image: '/static/categories/stories/resources/toolsAndSoftware.jpg',
    dispatchMethod: 'getResourceByType',
    sortOptions: [TOOLS_ALL, SOFTWARE, CALCULATORS]
  },
  {
    title: GAMES_TITLE,
    slug: 'games',
    image: '/static/categories/stories/resources/games.jpg',
    dispatchMethod: 'getProjectsByCategory',
    sortOptions: [POPULAR, GAMES_ALL]
  }
]
export default {
  name: 'exploreResources',
  components: {
    InfiniteScroll,
    ResourceCard
  },
  mixins: [utmTracking],
  data: function () {
    return {
      resourceCategories,
      projects: [],
      selectedCategory: {},
      selectedSort: '',
      hasMore: true,
      loading: false,
      offset: 0,
      limit: 12,
      params: {},
      error: '',
      message: '',
      UPCOMING,
      SOFTWARE,
      CALCULATORS,
      POPULAR,
      requestIterator: 0 // this is used to prevent displaying data for stale requests (quickly switch tabs can cause this)
    }
  },
  metaInfo: function () {
    return {
      title: `${this.$t('resource.explore.title')}`
    }
  },
  mounted: function () {
    this.$nextTick(() => {
      this.toggleSelectedCategory(this.resourceCategories[0])
    })
  },
  computed: {
    hasSelectedCategory: function () {
      return this.selectedCategory && this.selectedCategory.slug && this.selectedCategory.slug !== ''
    },
    isMobile: function () {
      return !userAgent.isApp() && (userAgent.isIOS() || userAgent.isAndroid())
    }
  },
  watch: {
    '$route.params.resourceSlug': function () {
      this.checkSlug(this.$route.params)
    }
  },
  methods: {
    setDefault: function () {
      const url = this.$route.params.resourceSlug ? this.$route.params.resourceSlug : ''
      let foundValidResourceSlug = false

      if (url !== '') {
        this.resourceCategories.forEach((resourceCategory) => {
          if (url === resourceCategory.slug) {
            foundValidResourceSlug = true
            this.toggleSelectedCategory(resourceCategory)
          }
        })
      }
      if (!foundValidResourceSlug) {
        this.toggleSelectedCategory(this.resourceCategories[0])
      }

      this.fetchProjects()
    },
    proxyImage: function (src) {
      return imageProxy.proxy(process.env.WEB_ROOT + src, { width: 120, height: 120, quality: 100 })
    },
    toggleSelectedCategory: function (category) {
      if (this.selectedCategory.slug !== category.slug) {
        this.selectedCategory = category
        if (this.selectedCategory.slug === 'toolsAndSoftware') {
          this.selectedSort = category.sortOptions[2]
        } else {
          this.selectedSort = category.sortOptions[0]
        }
        this.resetProjects()

        if (this.selectedCategory.slug === 'projects') {
          this.$router.push('/viewAll/?profile=mine')
        } else {
          this.$router.push({ name: 'resourcesType', params: { resourceSlug: category.slug } }).catch(() => {})
        }
      }
    },
    selectSortOption: function (sortOption) {
      if (sortOption !== this.selectedSort) {
        this.selectedSort = sortOption
        this.resetProjects()
      }
    },
    fetchProjects: function () {
      if (this.loading || !this.hasMore || !this.hasSelectedCategory || (this.selectedCategory.slug === 'toolsAndSoftware' && this.selectedSort === 'Software')) {
        return
      }

      this.loading = true
      this.requestIterator++
      this.buildParams()

      this.$store.dispatch(this.selectedCategory.dispatchMethod, this.params).then((response) => {
        if (this.requestIterator === response.requestIterator) {
          this.projects.push.apply(this.projects, response.projects)
          this.hasMore = response.hasMore
          this.offset += this.limit
          this.loading = false
          if (response.total === 0 && this.projects.length === 0) {
            this.message = this.$t('resource.explore.notFound')
          }
        }
      }, (error) => {
        this.loading = false
        this.error = error.message
        this.hasMore = false
      })
    },
    buildParams: function () {
      this.params = {
        limit: this.limit,
        offset: this.offset,
        sort: 'newest',
        iterator: this.requestIterator
      }

      if (!this.hasSelectedCategory) {
        return
      }
      switch (this.selectedCategory.slug) {
        case 'webinars':
          this.params.resourceSlug = this.selectedCategory.slug
          this.params.filter = this.selectedSort === this.UPCOMING ? 'future' : 'past'
          this.params.query = 'webinars'
          return
        case 'ebooksAndPapers':
          this.params.resourceSlug = this.selectedCategory.slug
          this.params.query = 'ebooksAndPapers'
          return
        case 'toolsAndSoftware':
          this.params.resourceSlug = this.selectedCategory.slug
          this.params.query = 'toolsAndSoftware'
          if (this.selectedSort === this.SOFTWARE) {
            this.params.filter = 'software'
          } else if (this.selectedSort === this.CALCULATORS) {
            this.params.filter = 'calculators'
          }
          this.params.sort = 'most_views'
          return
        case 'games':
          this.params.resourceSlug = 'stem-games'
          this.params.slug = 'stem-games'
          this.params.sort = this.selectedSort === this.POPULAR ? 'most_views' : 'alphabetically'
      }
    },
    loadMore: function () {
      if (this.hasMore && !this.loading) {
        this.fetchProjects()
      }
    },
    resetProjects: function () {
      this.hasMore = true
      this.offset = 0
      this.projects = []
      this.error = ''
      this.message = ''
      this.loading = false
      this.fetchProjects()
    },
    openProject: function (project) {
      if (project.url && project.url !== '') {
        if (project.url.includes('/GamesPuzzles') ||
            project.url.includes('/calculator') ||
            project.url.includes('games.')) {
          window.open(project.url)
        } else {
          window.open(this.getUTMLink(project.url, 'resource'))
        }
      } else {
        const routeName = 'viewStory'
        const routeParam = project.slug ? project.slug : project.id
        this.$router.push({ name: routeName, params: { id: routeParam } })
      }
    },
    getCardTitle: function (project) {
      switch (this.selectedCategory.slug) {
        case 'webinars':
          var webinarTime = moment.tz(project.resource.webinar.webinar_date, project.resource.webinar.webinar_time_zone)
          return webinarTime.format('MMMM DD YYYY, h:mmA z')
        default:
          return project.title
      }
    },
    getCardSubTitle: function (project) {
      switch (this.selectedCategory.slug) {
        case 'webinars':
          return project.title
        default:
          return ''
      }
    },
    checkSlug: async function (param) {
      if (param.resourceSlug) {
        const filteredSlug = resourceCategories.filter(categories => categories.slug.toLowerCase() === param.resourceSlug.toLowerCase())
        await this.toggleSelectedCategory(filteredSlug[0])
      } else {
        this.toggleSelectedCategory(resourceCategories[0])
      }
      if (param.resourceSort) {
        this.checkParamSort(param)
      }
    },
    checkParamSort: function (param) {
      switch (param.resourceSort) {
        case 'upcoming' :
          this.selectedSort = UPCOMING
          break
        case 'recorded' :
          this.selectedSort = RECORDED
          break
        case 'popular' :
          this.selectedSort = POPULAR
          break
        case 'allgames' :
          this.selectedSort = GAMES_ALL
          break
        case 'software' :
          this.selectedSort = SOFTWARE
          break
        case 'calculators' :
          this.selectedSort = CALCULATORS
          break
        case 'alltoolsandsoftware' :
          this.selectedSort = TOOLS_ALL
      }
    }
  }
}

</script>

<style lang="scss" scoped>
  @import '~styles/utils.scss';
  .resource-page {
    font-family: 'Noto Serif JP', serif;
    margin-top: 2rem;
    width: 100%;
    height: 100%;
    text-align: center;

    .gate-way-title {
      margin: 0 2rem;
    }

    .resource-title {
      display: inline-block;
      padding: 0 1rem 0.5rem;
      border-bottom: 1px solid $primary;
      margin-bottom: 1rem;
      font-family: $button-font;
      letter-spacing: 1.15px;
    }
    .category-row {
      justify-content: center;
      margin-top: 2rem;
      overflow-x: scroll;
      @media (max-width: 500px) {
        justify-content: left;
        margin-left: 1rem;
        margin-right: 1rem;
      }
      .category-container {
        margin-right: 2rem;
        @include mobile{
          margin-right: 1rem;
          padding: 0 0.5rem;
        }
        .category-image-shadow {
          margin: auto;
          height: 6rem;
          width: 6rem;
          box-shadow: $default-shadow;
          border-radius: 50%;
          overflow: hidden;

          .category-image-circle {
            width: 100%;
            height: 100%;
            clip-path: circle(50% at 50% 50%);
            &:hover, &:focus, &:active {
              img {
                transform: scale(1.1);
                @include touch {
                  transform: none;
                }
              }
            }
          }
        }

        .category-title{
          word-wrap: break-word;
          line-height: 1.3;
          font-size: 0.8rem;
          font-family: $button-font;
          font-weight: 400;
          margin: 0.75rem 0;
          @include mobile {
            font-size: 0.7rem;
          }
        }
      }
    }

    .is-inactive{
      opacity: 0.5;
    }

    .content-area {
      width: 96%;
      max-width: 1800px;
      min-height: 300px;
      margin: auto;
      margin-top: 2rem;
      background-color: #F2F2F2;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
      border-radius: 25px;
      padding: 2rem;
      @include mobile {
        margin-top: 1.25rem;
      }

      .sort-options {
        font-size: 0.8rem;
        justify-content: center;
        border-bottom: 1px solid #979797;
        font-family: $button-font;
        font-weight: 200;

        .sort-option{
          margin: 0 1rem;
          padding-bottom: 0.5rem;
          &.selected {
            color: $primary;
            border-bottom: 1px solid $primary;
          }
        }
      }

      .proj-cards-container {
        width: 100%;
        margin: 0 auto;

        .proj-cards-group {
          width: 100%;
          display: flex;
          flex-wrap: wrap;
          justify-content: space-evenly;

          .proj-card {
            margin: 2rem auto;
          }
        }
      }

      .loader {
        display: block;
        font-size: 2rem;
        margin: 1rem auto;
      }
    }

    .feedback-messages {
      &.is-mobile-browser {
        margin-bottom: 55px;
      }
    }
    .coming-soon {
      padding-top: 10px;
      font-size: 26px;
    }
  }
</style>
